// Angular
import { Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OfficeModel } from 'src/app/core/management';
import { BehaviorSubject } from 'rxjs';
import { AgreementTemplate } from 'src/app/core/agreement';

@Component({
  selector: 'kt-confirm-entity-details-dialog',
  templateUrl: './confirm-entity-details-dialog.component.html',
})
export class ConfirmEntityDetailsDialogComponent implements OnInit {
  // Public properties
  viewLoading = true;
  @ViewChild('confirmButton') confirmButton: ElementRef;
  @ViewChild('cancelButton') cancelButton: ElementRef;
  focusedButton = 'confirm';
  form: FormGroup;
  entity: any;
  contact: any;
  officesSet: Set<number> = new Set();
  offices: number[] = [];
  officeSelected = false;
  templatesSubject = new BehaviorSubject<AgreementTemplate[]>([]);
  templates$ = this.templatesSubject.asObservable();

  constructor(
    public dialogRef: MatDialogRef<ConfirmEntityDetailsDialogComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit() {
    this.contact = this.data.contact;
    this.entity = this.data.entity;

    // Only filter for offices that have templates
    this.data.templates.map((template) => template.offices.map((o) => this.officesSet.add(o)));
    this.offices = Array.from(this.officesSet);

    // get the office templates for a particular entity
    if (this.entity.office_id) {
      this.templatesSubject.next(this.getTemplatesFor(this.entity.office_id));
    }

    // Normalize and format the phone once at startup:
    const raw = (this.contact.cell_phone || '').replace(/\D/g, '');
    const formattedPhone =
      raw.length === 10
        ? `(${raw.substring(0, 3)}) ${raw.substring(3, 6)}-${raw.substring(6, 10)}`
        : '';

    this.form = this.fb.group({
      method: [''],
      contactEmail: [this.contact.email || '', Validators.email],
      contactPhone: [
        formattedPhone,
        [
          // only allow exactly "(123) 456-7890"
          Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/),
        ],
      ],
      agreementTemplate: [''],
      office: [this.entity.office_id || ''],
      fileUploadOffice: [this.entity.office_id || this.data.userPrimaryOffice],
      fileName: [''],
      file: [''],
    });

    this.officeSelected = !!this.entity.office_id;
    this.viewLoading = false;
  }

  @HostListener('window:keydown.enter', ['$event'])
  @HostListener('window:keydown.arrowleft', ['$event'])
  @HostListener('window:keydown.arrowright', ['$event'])
  onEnterKey(event: KeyboardEvent) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault(); // prevent default browser scrolling behavior
      this.focusedButton = this.focusedButton === 'confirm' ? 'cancel' : 'confirm';
    } else if (event.key === 'Enter') {
      if (this.focusedButton === 'confirm') {
        this.onYesClick();
      } else {
        this.onNoClick();
      }
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onOfficeChange(event): void {
    const templates = this.getTemplatesFor(event.value);
    this.templatesSubject.next(templates);
    this.officeSelected = true;
  }

  getTemplatesFor(officeId) {
    const templates = [];

    for (const t of this.data.templates) {
      if (t.offices.includes(officeId)) {
        templates.push(t);
      }
    }

    return templates;
  }

  onYesClick(): void {
    let hasErrors = false;
    const method = this.form.get('method').value;

    if (method === '') {
      this.form.get('method').markAsTouched();
      this.form.controls.method.setErrors({ required: true });
      hasErrors = true;
    }

    if (method === 'esignature') {
      // validate that a template is provided if esignature selected.
      if (this.form.controls.agreementTemplate.value === '') {
        this.form.controls.agreementTemplate.setErrors({ required: true });
        this.form.controls.agreementTemplate.markAsTouched();
        hasErrors = true;
      }

      // validate that an office is provided.
      if (this.form.controls.office.value === '') {
        this.form.controls.office.setErrors({ required: true });
        this.form.controls.office.markAsTouched();
        hasErrors = true;
      }

      // At least one value is required: email, phone or both.
      const emailControl = this.form.controls.contactEmail;
      const phoneControl = this.form.controls.contactPhone;

      // Require at least one of email or phone
      if (emailControl.value === '' && phoneControl.value === '') {
        emailControl.setErrors({ required: true });
        phoneControl.setErrors({ required: true });
        emailControl.markAsTouched();
        phoneControl.markAsTouched();
        hasErrors = true;
      } else {
        // If email is provided but invalid
        if (emailControl.value !== '' && emailControl.invalid) {
          emailControl.markAsTouched();
          hasErrors = true;
        }

        // If phone is provided but invalid
        if (phoneControl.value !== '' && phoneControl.invalid) {
          phoneControl.markAsTouched();
          hasErrors = true;
        }
      }
    }

    if (method === 'file') {
      // validate that a document is provided if file upload selected.
      if (this.form.controls.file.value === '') {
        this.form.controls.file.setErrors({ required: true });
        this.form.controls.file.markAsTouched();
        hasErrors = true;
      }
    }

    if (hasErrors) return;

    const data = {
      office_id:
        this.form.get('method').value == 'file'
          ? this.form.get('fileUploadOffice').value
          : this.form.get('office').value,
      template_id:
        this.form.get('method').value === 'esignature'
          ? this.form.get('agreementTemplate').value
          : '',
      file_base64: this.form.get('method').value === 'file' ? this.form.get('file').value : '',
      file_name: this.form.get('method').value === 'file' ? this.form.get('fileName').value : '',
      agreement_type_code: 'FINANCIAL_RESPONSIBILITY',
      data: {
        // Entity
        entity_id: this.data.entity.id,
        entity_name: `${this.data.entity.first_name} ${this.data.entity.last_name}`,
        entity_email: this.data.entity.email,
        // Contact
        contact_id: this.data.contact.id,
        contact_name: `${this.data.contact.first_name} ${this.data.contact.last_name}`,
        contact_email: this.form.get('contactEmail').value,
        contact_phone: this.form.get('contactPhone').value.replace(/\D/g, ''),
      },
    };

    this.dialogRef.close(data);
  }

  getOfficeName(officeId: number) {
    const offices: OfficeModel[] = this.data.offices.filter((o) => o.id === officeId);

    if (offices.length) {
      return offices[0].name;
    }

    return '';
  }

  changeFile(event) {
    const target = event.target as HTMLInputElement;

    if (target?.files?.length) {
      const file = target.files[0];
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = () => {
        this.form.get('file').patchValue((reader.result as string)?.split(',')[1]);
        this.form.get('fileName').patchValue(file.name.replace(/\.[0-9a-z]+$/i, ''));
      };
    }
  }
}
